import { useParams, Link } from "react-router-dom";
import {
 Container, Section, Breadcrumbs, Loader, ScheduleBtn, CallBtn, BtnPrimary,
 Clock, ArrowRight, ChevronRight,
} from "../components/common";
import { usePost, useBlog } from "../lib/api";
import { useSeo, breadcrumbLd, faqLd } from "../lib/seo";

export default function BlogDetail() {
 const { slug } = useParams();
 const { data: post, isLoading, isError } = usePost(slug);
 const { data: all = [] } = useBlog();

 useSeo({
 title: post?.metaTitle,
 description: post?.metaDescription,
 path: `/blog/${slug}`,
 image: post?.image,
 jsonLd: post ? [
 { "@context": "https://schema.org", "@type": "Article", headline: post.title, image: post.image,
 datePublished: post.date, author: { "@type": "Organization", name: "RPM Auto Repair" },
 publisher: { "@type": "Organization", name: "RPM Auto Repair" }, description: post.metaDescription },
 faqLd(post.faqs || []),
 breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${slug}` }]),
 ] : [],
 });

 if (isLoading) return <Loader />;
 if (isError || !post) return <Container className="py-32 text-center"><h1 className="font-display text-3xl text-white">Article Not Found</h1><div className="mt-6 flex justify-center"><BtnPrimary to="/blog">Back to Blog</BtnPrimary></div></Container>;

 const more = all.filter((p) => p.slug !== slug).slice(0, 3);

 return (
 <>
 <section className="relative overflow-hidden border-b border-white/10">
 <img src={post.image} alt={post.title} className="absolute inset-0 h-full w-full object-cover opacity-20" />
 <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/88 to-ink" />
 <Container className="relative py-14 sm:py-20">
 <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${slug}` }]} />
 <div className="flex items-center gap-3 text-xs tracking-wide text-primary"><Clock size={13} /> {post.readTime} · {post.author}</div>
 <h1 className="mt-4 max-w-4xl font-display text-3xl font-700 leading-[1.08] text-white sm:text-4xl lg:text-5xl">{post.title}</h1>
 </Container>
 </section>

 <Section>
 <Container className="grid gap-12 lg:grid-cols-[1fr_300px]">
 <div>
 {post.toc?.length > 0 && (
 <div className="mb-8 rounded-xl border border-white/10 bg-steel p-6">
 <h2 className="font-display text-lg text-white">Table of Contents</h2>
 <ol className="mt-3 space-y-1.5">
 {post.toc.map((t, i) => (
 <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground"><ChevronRight size={13} className="text-primary" /> {t}</li>
 ))}
 </ol>
 </div>
 )}
 <article className="prose-rpm max-w-none" data-testid="blog-content" dangerouslySetInnerHTML={{ __html: post.body }} />
 <p className="mt-8 rounded-lg border border-white/10 bg-steel p-4 text-xs text-muted-foreground">
 Sources & further reading: Manufacturer service guidelines, the ASE (National Institute for Automotive Service Excellence), the Car Care Council (carcare.org), and the California Bureau of Automotive Repair (BAR). Always consult your owner's manual for your specific vehicle.
 </p>
 </div>

 <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
 <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-[#1a1013] to-steel p-6">
 <h3 className="font-display text-xl text-white">Need Service?</h3>
 <p className="mt-2 text-sm text-muted-foreground">Talk to Riverside's trusted ASE-certified team. Free estimates, honest advice.</p>
 <div className="mt-5 space-y-3"><ScheduleBtn className="w-full" /><CallBtn className="w-full" /></div>
 </div>
 <div className="rounded-xl border border-white/10 bg-steel p-6">
 <h3 className="font-display text-lg text-white">More Articles</h3>
 <ul className="mt-4 space-y-3">
 {more.map((m) => (
 <li key={m.slug}><Link to={`/blog/${m.slug}`} className="group flex items-start gap-2 text-sm text-muted-foreground hover:text-primary">
 <ArrowRight size={14} className="mt-1 shrink-0 text-primary" /> {m.title}</Link></li>
 ))}
 </ul>
 </div>
 </aside>
 </Container>
 </Section>
 </>
 );
}
